import {Component, inject, OnInit, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {combineLatest} from 'rxjs';
import {ClassificationSchemeDto, FileTypeDto} from '|api/codebook';
import {ApiSettlementService, FileCloseDto, FileDto, FilePrepareCompletionFileTypeDto,} from '|api/document';
import {EntityClassDto, InternalNotificationKey, RetentionTriggerTypeCode} from '|api/commons';
import {VisibleClassifiersMode} from '|shared';
import {
  AbstractFileSettleCloseDialogComponent
} from '../file-settle-dialog/abstract-file-settle-close-dialog.component';
import {
  ButtonComponent,
  FormButtonsComponent,
  TabDirective,
  TabsComponent,
  WaitingDirective
} from '@icz/angular-essentials';
import {formatAsLocalIsoDate, IczFormControl, IczFormGroup, IczOption, IczValidators} from '@icz/angular-form-elements';
import {injectModalData, injectModalRef} from '@icz/angular-modal';
import {IczEmptyDatasource, IczInMemoryDatasource, IczTableDataSource} from '@icz/angular-table';
import {FileSettleStateService} from '../file-settle-dialog/file-settle-state.service';
import {GeneralFileCloseFormComponent} from './general-file-close-form/general-file-close-form.component';
import {FileCloseFormComponent} from './file-close-form/file-close-form.component';
import {
  CheckUnsavedFormDialogService,
  IczFormSubmitDirective,
  IFormGroupCheckable,
  namedDtoToOption
} from '@icz/angular-application-core';
import {FileToastService, FileToastType} from '|ui-essl-core';
import {AsyncPipe} from '@angular/common';

export function getFileCloseForm(): IczFormGroup {
  return new IczFormGroup({
    entityClassId: new IczFormControl<Nullable<number>>(null, [IczValidators.required()]),
    fileTypeId: new IczFormControl<Nullable<number>>(null),
    closeDate: new IczFormControl<Nullable<string>>({value: formatAsLocalIsoDate(new Date()), disabled: true}),
    disposalOperationCode: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    disposalYear: new IczFormControl<Nullable<number>>({value: null, disabled: true}),
    disposalScheduleId: new IczFormControl<Nullable<number>>({value: null, disabled: true}),
    yearOfRetentionPeriodStart: new IczFormControl<Nullable<string>>({value: null, disabled: true}),
    retentionTriggerTypeCode: new IczFormControl<Nullable<RetentionTriggerTypeCode>>({value: null, disabled: true}),
    triggerEventCheckYear: new IczFormControl<Nullable<string>>({value: null, disabled: true}, [IczValidators.min((new Date()).getFullYear())]),
    externalRetentionTriggerIds: new IczFormControl<Nullable<string>>(null),
    entityClassValidFrom: new IczFormControl<Nullable<string>>({value: null, disabled: true}, []),
    entityClassValidTo: new IczFormControl<Nullable<string>>({value: null, disabled: true}, []),
  });
}

@Component({
  selector: 'icz-file-close-dialog',
  templateUrl: './file-close-dialog.component.html',
  styleUrls: ['./file-close-dialog.component.scss'],
  providers: [
    CheckUnsavedFormDialogService,
    FileSettleStateService
  ],
  imports: [TabsComponent, WaitingDirective, TabDirective, GeneralFileCloseFormComponent, FileCloseFormComponent, FormButtonsComponent, ButtonComponent, IczFormSubmitDirective, AsyncPipe]
})
export class FileCloseDialogComponent extends AbstractFileSettleCloseDialogComponent implements OnInit, IFormGroupCheckable {

  protected modalRef = injectModalRef<Nullable<boolean>>();
  private checkUnsavedService = inject(CheckUnsavedFormDialogService);
  private apiSettlementService = inject(ApiSettlementService);
  protected fileToastService = inject(FileToastService);
  protected override objects = injectModalData<FileDto[]>();

  @ViewChild('tabsComponent') tabsComponent!: TabsComponent;

  override generalTabForm = getFileCloseForm();

  singleFileSettlementDate!: Date;

  formGroupsToCheck!: string[];
  fileTypes: FileTypeDto[] = [];
  classificationSchemes: ClassificationSchemeDto[] = [];
  allEntityClasses: EntityClassDto[] = [];
  availableEntityClasses: Record<string, EntityClassDto[]> = {};
  availableFileTypes: Record<string, FilePrepareCompletionFileTypeDto[]> = {};
  isInitialized = false;

  generalTabEntityClassOptions: IczOption[] = [];
  generalTabFileTypeOptions: IczOption[] = [];
  allEntityClassesDataSource: IczTableDataSource<EntityClassDto> = new IczEmptyDatasource();

  readonly VisibleClassifiersMode = VisibleClassifiersMode;

  get submitLabel() {
    return this.isBulk ? 'Uzavřít spisy' : 'Uzavřít spis';
  }

  override ngOnInit(): void {
    super.ngOnInit();

    if (!this.objects?.length) return;
    this.isBulk = this.objects.length > 1;
    this.checkUnsavedService.addUnsavedFormCheck(this, ['generalTabForm']);
    this.initTabs();

    this.initForms();

    if (this.isBulk) {
      this.initBulkCloseGeneralForm();
      this.loadingService.doLoading(
        combineLatest([
          this.apiFileService.filePrepareClose({body: this.objects.map(f => f.id)}),
          this.codebookService.classificationSchemes(),
          this.codebookService.entityClasses(),
        ]),
        this
      ).subscribe(([
          bulkFilePrepare,
          classificationSchemes,
          entityClasses
        ]) => {
        this.allEntityClassesDataSource = new IczInMemoryDatasource(() => bulkFilePrepare.availableEntityClasses!);
        this.generalTabForm.get('disposalOperationCode')!.setValue(bulkFilePrepare.disposalOperationCode);
        this.generalTabEntityClassOptions = bulkFilePrepare.availableEntityClasses!.map(ec => namedDtoToOption()(ec));
        this.generalTabFileTypeOptions = bulkFilePrepare.availableFileTypes!.map(ft => {return {label: ft.label!, value: ft.id!};});

        bulkFilePrepare.filePrepare!.forEach(fp => {
          this.availableEntityClasses[fp.fileId!] = fp.availableEntityClasses!;
          this.availableFileTypes[fp.fileId!] = fp.availableFileTypes!;
          this.forms[String(fp.fileId!)].get('retentionPeriod')!.setValue(fp.retentionPeriod);
          this.forms[String(fp.fileId!)].get('disposalOperationCode')!.setValue(fp.disposalOperationCode);
        });

        this.allEntityClasses = entityClasses;
        this.classificationSchemes = classificationSchemes;
        this.isInitialized = true;

        this.bulkPrepareRetentionSectionAndDisposalData();
      });

    } else {
      this.loadingService.doLoading(
        combineLatest([
          this.apiFileService.filePrepareClose({body: [this.objects[0].id]}),
          this.apiSettlementService.settlementFindById({id: this.objects[0].settlementId!}),
          this.codebookService.classificationSchemes(),
          this.codebookService.entityClasses(),
        ]),
        this
      ).subscribe(([filePrepare, fileSettlement, classificationSchemes, entityClasses]) => {
        this.singleFileSettlementDate = new Date(fileSettlement.settlementDate!);
        this.classificationSchemes = classificationSchemes;
        this.allEntityClasses = entityClasses;
        this.availableFileTypes[String(this.objects[0].id)] = filePrepare.availableFileTypes!;
        this.availableEntityClasses[String(this.objects[0].id)] = filePrepare.availableEntityClasses!;
        this.isInitialized = true;
      });
    }
  }

  private initForms() {
    this.objects.forEach(f => {
      const newFileForm = getFileCloseForm();
      newFileForm.get('fileTypeId')!.setValue(f.fileTypeId);
      newFileForm.get('entityClassId')!.setValue(f.entityClassId);
      this.forms[f.id] = newFileForm;
    });
  }

  private bulkPrepareRetentionSectionAndDisposalData() {
    this.apiFileService.fileGetValidRetentionTriggerData({body: this.objects.map(obj => ({
        entityClassId: this.forms[obj.id].get('entityClassId')!.value,
        entityId: obj.id
      }))}).subscribe(validRetentions => {
      this.patchBulkObjectRetention(validRetentions);
      this.updateBulkDisposalData();
    });
  }

  private initBulkCloseGeneralForm() {
    this.generalTabForm.get('entityClassId')!.clearValidators();
    this.generalTabForm.get('disposalScheduleId')!.clearValidators();

    if (this.objects.every(d => d.entityClassId === this.objects[0].entityClassId)) {
      this.generalTabForm.get('entityClassId')!.setValue(this.objects[0].entityClassId);
    }
    if (this.objects.every(d => d.disposalScheduleId === this.objects[0].disposalScheduleId)) {
      this.generalTabForm.get('disposalScheduleId')!.setValue(this.objects[0].disposalScheduleId);
    }
    if (this.objects.every(d => d.fileTypeId === this.objects[0].fileTypeId)) {
      this.generalTabForm.get('fileTypeId')!.setValue(this.objects[0].fileTypeId);
    }

    this.generalTabForm.addControl('useGlobalSettings', new IczFormControl(false));

    this.generalTabForm.get('entityClassId')!.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(value => {
      const entityClass = this.allEntityClasses.find(ec => ec.id === value)!;
      this.generalTabForm.get('entityClassValidFrom')!.setValue(entityClass!.validFrom);
      this.generalTabForm.get('entityClassValidTo')!.setValue(entityClass!.validTo);
      this.bulkPrepareRetentionSectionAndDisposalData();
    });
    const overrideFields: string[] = [
      'entityClassId',
      'fileTypeId',
      'disposalScheduleId',
    ];
    this.setupGeneralTabValueOverride(overrideFields);
  }

  submit() {
    if (this.isBulk) {
      this.toggleShowValidity(true);

      const allFormsValid = Object.values(this.forms).every(f => f.valid);
      if (allFormsValid) {
        const closeDtos: FileCloseDto[] = [];
        Object.entries(this.forms).forEach(([fileId, form]) => {
          const formValue = form.getRawValue() as FileCloseDto;
          closeDtos.push({
            fileId: parseInt(fileId),
            fileTypeId: formValue.fileTypeId!,
            closureDate: formValue.closureDate,
            disposalScheduleId: formValue.disposalScheduleId,
            entityClassId: formValue.entityClassId,
            triggerEventCheckYear: formValue.triggerEventCheckYear,
            yearOfRetentionPeriodStart: formValue.yearOfRetentionPeriodStart,
          });
        });
        this.loadingService.doLoading(
          this.apiFileService.fileBulkCloseFiles({body: closeDtos}),
          this,
        ).subscribe(_ => {
          this.fileToastService.dispatchBulkOperationStartedToast(FileToastType.BULK_FILE_SETTLEMENT_CLOSE_SUCCESS, {
            [InternalNotificationKey.COUNT]: this.objects.length,
          });
          this.modalRef.close(true);
        });
      } else {
        this.markFirstInvalidFormAsTouched();
        this.tabsComponent.selectAndScrollToFirstInvalid();
      }
    } else {
      const formValue = this.getFileTabForm(this.objects[0].id).getRawValue();
      const requestDto: FileCloseDto = {
        entityClassId: formValue.entityClassId!,
        fileTypeId: formValue.fileTypeId!,
        disposalScheduleId: formValue.disposalScheduleId!,
        triggerEventCheckYear: formValue.triggerEventCheckYear,
        yearOfRetentionPeriodStart: formValue.yearOfRetentionPeriodStart,
      };

      this.loadingService.doLoading(
        this.apiFileService.fileCloseFile({
          id: this.objects[0].id,
          body: requestDto,
        }),
        this,
      ).subscribe( _ => {
        this.fileToastService.dispatchFileInfoToast(FileToastType.FILE_CLOSED, {
          [InternalNotificationKey.FILE_ID]: this.objects[0].id,
          [InternalNotificationKey.FILE_SUBJECT]: this.objects[0].subject!,
        });
        this.modalRef.close(true);
      });
    }
  }

  cancel() {
    this.modalRef.close(null);
  }

}
